import React, {
  memo,
  useRef,
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from "react";
import { View, StyleSheet, Animated } from "react-native";
import {
  DndContext,
  useDraggable,
  useDroppable,
  pointerWithin,
  rectIntersection,
} from "@dnd-kit/core";
import { useExerciseDrag } from "../../../contexts/ExerciseDragContext";
import { useExerciseDragKit } from "./useExerciseDragKit";
import { useToast } from "../../../contexts/ToastContext";
import Colors from "../../../../Resources/Color";
import {
  normalizeProtocolSlot,
  isCrossWeekBlocked,
  showBlockedMoveToast,
  clearBlockedMoveToast,
  calculateDropPosition,
  getDOMElement,
  executeMove,
  dropIndicatorStyles,
  DROP_INDICATOR_HEIGHT,
} from "./exerciseDragUtils";

// ============================================================================
// Global State
// ============================================================================

/**
 * Global map to track drop positions for each exercise during drag.
 * Key: `exercise-${workoutId}-${index}`
 * Value: "above" | "below" | null
 */
const dropPositionMap = new Map();

// ============================================================================
// Styles (using shared styles, adding platform-specific ones)
// ============================================================================

const dragStyles = StyleSheet.create({
  exerciseWrapper: {
    position: "relative",
  },
  exerciseDragging: {
    opacity: 0.3,
    pointerEvents: "none",
  },
  dropIndicator: {
    position: "absolute",
    left: 4,
    right: 4,
    height: DROP_INDICATOR_HEIGHT,
    backgroundColor: Colors.bubbleColor,
    borderRadius: 3,
    zIndex: 100,
  },
  dropIndicatorCircle: {
    position: "absolute",
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.bubbleColor,
    top: -2.5,
  },
  dropIndicatorCircleLeft: {
    left: -4,
  },
  dropIndicatorCircleRight: {
    right: -4,
  },
  dropZoneActive: {
    backgroundColor: Colors.lightGrayColor,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    borderStyle: "dashed",
    borderRadius: 8,
  },
});

// ============================================================================
// Drop Zone Components
// ============================================================================

/**
 * Drop zone between exercises for reordering.
 * Shows a drop indicator line when a dragged exercise is hovered over it.
 */
export const ExerciseDropZone = memo(
  ({ workoutId, workoutWeek, position, gap = 8 }) => {
    const indicatorOffset = Math.max(0, (gap - DROP_INDICATOR_HEIGHT) / 2);

    const { setNodeRef, isOver, active } = useDroppable({
      id: `drop-${workoutId}-${position}`,
      data: {
        type: "exerciseDropZone",
        workoutId,
        position,
        workoutWeek,
      },
    });

    // Check if this is a valid drop (not blocked by week restriction)
    const activeData = active?.data?.current;
    const isBlocked = activeData
      ? isCrossWeekBlocked(activeData, workoutWeek)
      : false;
    const showIndicator = isOver && !isBlocked && active;

    return (
      <View ref={setNodeRef} style={{ height: gap, position: "relative" }}>
        <View
          style={[
            dropIndicatorStyles.dropIndicator,
            { top: indicatorOffset, opacity: showIndicator ? 1 : 0 },
          ]}
          pointerEvents="none"
        >
          <View
            style={[
              dropIndicatorStyles.dropIndicatorCircle,
              dropIndicatorStyles.dropIndicatorCircleLeft,
            ]}
          />
          <View
            style={[
              dropIndicatorStyles.dropIndicatorCircle,
              dropIndicatorStyles.dropIndicatorCircleRight,
            ]}
          />
        </View>
      </View>
    );
  },
);

/**
 * Trailing drop zone at the end of an exercise list.
 * Shows a drop indicator when dragging to the end of a workout.
 */
export const TrailingDropZone = memo(
  ({ workoutId, workoutWeek, exerciseCount, gap = 8 }) => {
    const indicatorOffset = Math.max(0, (gap - DROP_INDICATOR_HEIGHT) / 2);
    const { showToast, hideToast } = useToast();

    const { setNodeRef, isOver, active } = useDroppable({
      id: `trailing-${workoutId}`,
      data: {
        type: "trailingDropZone",
        workoutId,
        workoutWeek,
        position: exerciseCount, // Insert at end
      },
    });

    const activeData = active?.data?.current;

    // Compute canDrop logic matching react-dnd implementation
    const canDrop = activeData
      ? (() => {
          // Blocked by protocol slot restriction
          if (isCrossWeekBlocked(activeData, workoutWeek)) return false;

          // If same workout, check if it's the last exercise (no-op)
          if (activeData.workoutId === workoutId) {
            const endIndex = Math.max(0, exerciseCount - 1);
            // Allow dropping if it's not the last exercise (can reorder)
            // Or if there's only one exercise, allow dropping from other workouts
            return activeData.index !== endIndex;
          }

          // Cross-workout drops are always allowed (if not blocked by protocol)
          return true;
        })()
      : false;

    const isProtocolBlocked = activeData
      ? isCrossWeekBlocked(activeData, workoutWeek)
      : false;

    // Show indicator when hovering AND can drop (matching react-dnd logic)
    const showIndicator = isOver && canDrop;

    // Show blocked toast ONLY when hovering over blocked zone due to protocol slot restriction
    // Toast will remain visible until drag ends (cleared in drag end callback)
    // IMPORTANT: Only show toast for protocol slot blocking, not for other reasons (e.g., same workout, last exercise)
    useEffect(() => {
      if (isOver && isProtocolBlocked) {
        showBlockedMoveToast(showToast);
      }
      // Don't clear toast here - let it persist until drag ends
    }, [isOver, isProtocolBlocked, showToast]);

    return (
      <View style={dropIndicatorStyles.trailingDropWrapper}>
        <View ref={setNodeRef} style={dropIndicatorStyles.trailingDropHitArea}>
          <View
            style={[
              dropIndicatorStyles.dropIndicator,
              {
                top: indicatorOffset,
                left: 8,
                right: 8,
                opacity: showIndicator ? 1 : 0,
              },
            ]}
            pointerEvents="none"
          >
            <View
              style={[
                dropIndicatorStyles.dropIndicatorCircle,
                dropIndicatorStyles.dropIndicatorCircleLeft,
              ]}
            />
            <View
              style={[
                dropIndicatorStyles.dropIndicatorCircle,
                dropIndicatorStyles.dropIndicatorCircleRight,
              ]}
            />
          </View>
        </View>
      </View>
    );
  },
);

/**
 * Drop zone for empty workouts.
 * Shows a dashed border when a dragged exercise is hovered over an empty workout.
 */
export const EmptyWorkoutDropZone = memo(
  ({ workoutId, workoutWeek, children, style }) => {
    const { showToast, hideToast } = useToast();

    const { setNodeRef, isOver, active } = useDroppable({
      id: `empty-${workoutId}`,
      data: {
        type: "emptyWorkoutDropZone",
        workoutId,
        workoutWeek,
        position: 0,
      },
    });

    const activeData = active?.data?.current;
    const isBlocked = activeData
      ? isCrossWeekBlocked(activeData, workoutWeek)
      : false;
    const isSameWorkout = activeData?.workoutId === workoutId;

    // Only show active state for cross-workout drops (same workout would be a no-op)
    const showActive = isOver && !isBlocked && active && !isSameWorkout;

    // Show blocked toast
    useEffect(() => {
      if (isOver && isBlocked) {
        showBlockedMoveToast(showToast);
      }
    }, [isOver, isBlocked, showToast]);

    return (
      <View ref={setNodeRef} style={style}>
        <View
          style={[
            dragStyles.dropZoneActive,
            {
              opacity: showActive ? 1 : 0,
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
            },
          ]}
        />
        {children}
      </View>
    );
  },
);

// ============================================================================
// Draggable Exercise Component
// ============================================================================

/**
 * Draggable exercise card wrapper.
 *
 * Makes an exercise card draggable and shows drop indicators (above/below)
 * based on pointer position when hovering during drag.
 *
 * Features:
 * - Drag handle integration via dragListeners prop
 * - Visual feedback (opacity change when dragging)
 * - Drop position detection (top 50% = above, bottom 50% = below)
 * - Protocol slot week restriction validation
 */
export const DraggableExercise = memo(
  ({
    exercise,
    idx,
    workoutId,
    workoutWeek,
    gap = 8,
    children,
    renderDragHandle,
  }) => {
    const containerRef = useRef(null);
    const [dropPosition, setDropPosition] = useState(null);
    const { showToast, hideToast } = useToast();
    const movedExerciseIds = useMovedExerciseIds();
    const pointerPosition = usePointerPosition();
    const scaleAnim = useRef(new Animated.Value(1)).current;
    const prevMovedIdsRef = useRef(new Set());

    // Rect cache for getBoundingClientRect
    const rectCacheRef = useRef({ rect: null, timestamp: 0 });
    const RECT_CACHE_TTL = 100; // ms

    // Simple scale animation when exercise is moved
    // Animates whenever the exercise enters the moved set
    useEffect(() => {
      const isMoved = movedExerciseIds.has(exercise?.id);
      const wasMoved = prevMovedIdsRef.current.has(exercise?.id);

      // If exercise just entered the moved set (wasn't there before), animate it
      if (isMoved && !wasMoved) {
        // Scale up then back down for visual feedback
        Animated.sequence([
          Animated.timing(scaleAnim, {
            toValue: 1.02,
            duration: 150,
            useNativeDriver: true,
          }),
          Animated.timing(scaleAnim, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
        ]).start();
      }

      // Update previous moved IDs for next comparison
      // This ensures we detect when exercise enters/leaves the moved set
      prevMovedIdsRef.current = new Set(movedExerciseIds);
    }, [exercise?.id, movedExerciseIds, scaleAnim]);

    // Calculate indicator offset
    const indicatorOffset = (gap + DROP_INDICATOR_HEIGHT) / 2;

    // Setup draggable
    const {
      attributes,
      listeners,
      setNodeRef: setDraggableRef,
      transform,
      isDragging,
    } = useDraggable({
      id: exercise?.id,
      data: {
        type: "exercise",
        exerciseId: exercise?.id,
        workoutId,
        index: idx,
        protocolSlot: normalizeProtocolSlot(exercise?.protocolSlot),
        sourceWeek: workoutWeek,
      },
    });

    // Setup droppable for this exercise (for above/below detection)
    const {
      setNodeRef: setDroppableRef,
      isOver,
      active,
    } = useDroppable({
      id: `exercise-${workoutId}-${idx}`,
      data: {
        type: "exerciseTarget",
        workoutId,
        workoutWeek,
        index: idx,
        exerciseId: exercise?.id,
      },
    });

    // Merge refs
    const mergedRef = useCallback(
      (node) => {
        containerRef.current = node;
        setDraggableRef(node);
        setDroppableRef(node);
        // Invalidate rect cache when ref changes
        rectCacheRef.current = { rect: null, timestamp: 0 };
      },
      [setDraggableRef, setDroppableRef],
    );

    // Get cached rect with TTL
    const getCachedRect = useCallback(() => {
      if (!containerRef.current) return null;

      const element = getDOMElement(containerRef.current);
      if (!element || typeof element.getBoundingClientRect !== "function") {
        return null;
      }

      const now = Date.now();
      // Return cached rect if still valid
      if (
        rectCacheRef.current.rect &&
        now - rectCacheRef.current.timestamp < RECT_CACHE_TTL
      ) {
        return rectCacheRef.current.rect;
      }

      // Calculate and cache new rect
      const rect = element.getBoundingClientRect();
      rectCacheRef.current = { rect, timestamp: now };
      return rect;
    }, []);

    // Track pointer position during drag to determine top/bottom half
    // Uses centralized pointer position from context instead of individual listeners
    useEffect(() => {
      if (!isOver || !active || isDragging || !containerRef.current) {
        setDropPosition(null);
        return;
      }

      // Don't show indicator on the item being dragged
      const activeData = active.data.current;
      if (activeData?.workoutId === workoutId && activeData?.index === idx) {
        setDropPosition(null);
        return;
      }

      // Check if blocked
      if (isCrossWeekBlocked(activeData, workoutWeek)) {
        setDropPosition(null);
        showBlockedMoveToast(showToast);
        return;
      }

      // Use centralized pointer position from context
      const rect = getCachedRect();
      if (!rect) return;

      const pointerY = pointerPosition.y;
      // Only check for undefined - 0 is a valid Y coordinate (top of screen)
      if (pointerY === undefined) return;

      // Use shared utility to calculate drop position
      const newDropPosition = calculateDropPosition(pointerY, rect);

      setDropPosition(newDropPosition);

      // Store in global map for handleDragEnd to access
      const mapKey = `exercise-${workoutId}-${idx}`;
      dropPositionMap.set(mapKey, newDropPosition);
    }, [
      isOver,
      active,
      isDragging,
      workoutId,
      idx,
      workoutWeek,
      showToast,
      pointerPosition,
      getCachedRect,
    ]);

    // Clear drop position when not hovering
    useEffect(() => {
      if (!isOver) {
        setDropPosition(null);
        const mapKey = `exercise-${workoutId}-${idx}`;
        dropPositionMap.delete(mapKey);
        // Invalidate rect cache when not hovering
        rectCacheRef.current = { rect: null, timestamp: 0 };
      }
    }, [isOver, workoutId, idx]);

    // Check if we should show drop indicator
    const activeData = active?.data?.current;
    const isBlocked = activeData
      ? isCrossWeekBlocked(activeData, workoutWeek)
      : false;
    const showDropIndicator =
      isOver && dropPosition && !isDragging && !isBlocked;

    return (
      <Animated.View
        ref={mergedRef}
        style={[
          dragStyles.exerciseWrapper,
          isDragging && dragStyles.exerciseDragging,
          !isDragging && {
            transform: [{ scale: scaleAnim }],
          },
        ]}
        {...attributes}
      >
        {/* Drop indicator - above */}
        {dropPosition === "above" && (
          <View
            style={[
              dragStyles.dropIndicator,
              { top: -indicatorOffset, opacity: showDropIndicator ? 1 : 0 },
            ]}
            pointerEvents="none"
          >
            <View
              style={[
                dragStyles.dropIndicatorCircle,
                dragStyles.dropIndicatorCircleLeft,
              ]}
            />
            <View
              style={[
                dragStyles.dropIndicatorCircle,
                dragStyles.dropIndicatorCircleRight,
              ]}
            />
          </View>
        )}

        {/* The actual exercise card */}
        {typeof children === "function"
          ? children({ dragListeners: listeners, isDragging })
          : children}

        {/* Drop indicator - below */}
        {dropPosition === "below" && (
          <View
            style={[
              dragStyles.dropIndicator,
              { bottom: -indicatorOffset, opacity: showDropIndicator ? 1 : 0 },
            ]}
            pointerEvents="none"
          >
            <View
              style={[
                dragStyles.dropIndicatorCircle,
                dragStyles.dropIndicatorCircleLeft,
              ]}
            />
            <View
              style={[
                dragStyles.dropIndicatorCircle,
                dragStyles.dropIndicatorCircleRight,
              ]}
            />
          </View>
        )}
      </Animated.View>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.exercise?.id !== nextProps.exercise?.id) return false;
    if (prevProps.idx !== nextProps.idx) return false;
    if (prevProps.workoutId !== nextProps.workoutId) return false;
    if (prevProps.gap !== nextProps.gap) return false;
    return true;
  },
);

// ============================================================================
// Collision Detection
// ============================================================================

/**
 * Custom collision detection that prioritizes drop zones over exercise targets.
 * This ensures drop zones (between exercises, trailing, empty) are detected
 * before individual exercise targets.
 */
export const exerciseCollisionDetection = (args) => {
  const { pointerCoordinates, droppableContainers } = args;
  const EMPTY_WORKOUT_TOP_THRESHOLD = 20; // Only detect empty workout drops within 20px of top

  // First check for drop zones (between exercises, trailing, empty)
  const pointerCollisions = pointerWithin(args);

  if (pointerCollisions.length > 0) {
    // Separate all collisions by type
    const exerciseTargets = pointerCollisions.filter((collision) => {
      const type = collision.data?.droppableContainer?.data?.current?.type;
      return type === "exerciseTarget";
    });

    const dropZones = pointerCollisions.filter((collision) => {
      const type = collision.data?.droppableContainer?.data?.current?.type;
      return (
        type === "exerciseDropZone" ||
        type === "trailingDropZone" ||
        type === "emptyWorkoutDropZone"
      );
    });

    // Filter drop zones based on pointer position
    let filteredDropZones = dropZones;
    if (pointerCoordinates && dropZones.length > 0) {
      filteredDropZones = dropZones.filter((collision) => {
        const type = collision.data?.droppableContainer?.data?.current?.type;
        const container = collision.data?.droppableContainer;
        const rect = container?.rect;

        if (!rect || !pointerCoordinates.y) return true;

        if (type === "emptyWorkoutDropZone") {
          // Only detect if within 20px of the top
          const distanceFromTop = pointerCoordinates.y - rect.top;
          return distanceFromTop <= EMPTY_WORKOUT_TOP_THRESHOLD;
        }

        if (type === "trailingDropZone") {
          // Detect trailing zones if pointer is anywhere within the zone bounds
          const distanceFromZoneTop = pointerCoordinates.y - rect.top;
          const zoneHeight = rect.height || 150;
          // Allow detection throughout the entire hit area
          return distanceFromZoneTop >= 0 && distanceFromZoneTop <= zoneHeight;
        }

        return true; // Keep all other drop zones
      });
    }

    if (filteredDropZones.length > 0) {
      // Separate trailing zones from other drop zones
      const trailingZones = filteredDropZones.filter((collision) => {
        return (
          collision.data?.droppableContainer?.data?.current?.type ===
          "trailingDropZone"
        );
      });
      const nonTrailingZones = filteredDropZones.filter((collision) => {
        return (
          collision.data?.droppableContainer?.data?.current?.type !==
          "trailingDropZone"
        );
      });

      // If we have trailing zones and pointer coordinates, prioritize them when pointer is in trailing area
      if (trailingZones.length > 0 && pointerCoordinates) {
        // Check if pointer is within any trailing zone bounds
        const pointerInTrailingArea = trailingZones.some((collision) => {
          const container = collision.data?.droppableContainer;
          const rect = container?.rect;
          if (!rect || !pointerCoordinates.y) return false;

          const zoneHeight = rect.height || 150;
          const distanceFromTop = pointerCoordinates.y - rect.top;
          // If pointer is anywhere in the trailing zone, prioritize it
          return distanceFromTop >= 0 && distanceFromTop <= zoneHeight;
        });

        // If pointer is in trailing area, prioritize trailing zones
        // This ensures trailing zones work even when there are exercise targets nearby
        if (pointerInTrailingArea) {
          return trailingZones;
        }
      }

      // Otherwise, prioritize non-trailing zones first (they're more precise)
      if (nonTrailingZones.length > 0) {
        return nonTrailingZones;
      }

      // If only trailing zones, return them
      return trailingZones;
    }

    // If no drop zones, return exercise targets
    if (exerciseTargets.length > 0) {
      return exerciseTargets;
    }

    return pointerCollisions;
  }

  // Fallback to rect intersection
  return rectIntersection(args);
};

// ============================================================================
// Moved Exercise IDs Context
// ============================================================================

/**
 * Context to track which exercise IDs were just moved (for animation).
 * Only exercises in this set will animate when their position changes.
 */
const MovedExerciseIdsContext = createContext(new Set());

/**
 * Hook to access moved exercise IDs
 */
const useMovedExerciseIds = () => useContext(MovedExerciseIdsContext);

// ============================================================================
// Pointer Position Context (for centralized pointer tracking)
// ============================================================================

/**
 * Context to share pointer position during drag.
 * This allows a single global listener instead of per-component listeners.
 */
const PointerPositionContext = createContext({ x: 0, y: 0 });

/**
 * Hook to access current pointer position during drag
 */
const usePointerPosition = () => useContext(PointerPositionContext);

// ============================================================================
// Exercise DnD Provider
// ============================================================================

/**
 * Exercise Drag & Drop Context Provider.
 *
 * Wraps all workout cards and provides drag & drop functionality for exercises.
 * Handles both same-workout reordering and cross-workout moves.
 *
 * Features:
 * - Auto-scroll at viewport edges
 * - Cursor styling (move cursor during drag)
 * - Drop position tracking (above/below based on pointer position)
 * - Protocol slot week restriction enforcement
 * - Toast notifications for blocked moves
 * - Animation tracking for moved exercises only
 *
 * @param {Function} onMoveExercise - Callback for same-workout reordering
 * @param {Function} onMoveExerciseCrossWorkout - Callback for cross-workout moves
 */
export const ExerciseDndProvider = ({
  children,
  onMoveExercise,
  onMoveExerciseCrossWorkout,
}) => {
  const [movedExerciseIds, setMovedExerciseIds] = useState(new Set());
  const [pointerPosition, setPointerPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const {
    startAutoScroll,
    stopAutoScroll,
    updateDragPosition,
    setActiveDragItem,
    clearActiveDragItem,
  } = useExerciseDrag();

  const { showToast, hideToast } = useToast();

  // Refs for RAF throttling
  const rafIdRef = useRef(null);
  const pendingPositionRef = useRef(null);

  // Setup drag kit hooks
  const {
    sensors,
    handleDragStart: kitDragStart,
    handleDragMove: kitDragMove,
    handleDragEnd: kitDragEnd,
    handleDragCancel: kitDragCancel,
  } = useExerciseDragKit({
    startAutoScroll,
    stopAutoScroll,
    updateDragPosition,
  });

  // Global pointer move handler with RAF throttling
  const handleGlobalPointerMove = useCallback(
    (e) => {
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      const y = e.clientY ?? e.touches?.[0]?.clientY;
      if (x === undefined || y === undefined) return;

      // Store pending position
      pendingPositionRef.current = { x, y };

      // Schedule update via RAF (throttles to 60fps max)
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(() => {
          if (pendingPositionRef.current) {
            setPointerPosition(pendingPositionRef.current);
            // Also update for auto-scroll
            updateDragPosition(
              pendingPositionRef.current.x,
              pendingPositionRef.current.y,
            );
          }
          rafIdRef.current = null;
          pendingPositionRef.current = null;
        });
      }
    },
    [updateDragPosition],
  );

  // Set up global pointer listener ONLY when dragging
  // This updates PointerPositionContext and calls updateDragPosition for auto-scroll
  // Note: ExerciseDragContext also has its own listeners for auto-scroll, but these
  // are needed for PointerPositionContext which is used by drop zones for position calculation
  useEffect(() => {
    if (!isDragging) {
      // Not dragging: ensure listeners are removed and clean up any pending RAF
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      pendingPositionRef.current = null;
      return;
    }

    // Only attach listeners when actively dragging
    if (typeof document !== "undefined") {
      document.addEventListener("pointermove", handleGlobalPointerMove, {
        passive: true,
      });
      // Also listen for touch events for mobile
      document.addEventListener("touchmove", handleGlobalPointerMove, {
        passive: true,
      });

      // Cleanup: remove listeners when drag ends or component unmounts
      return () => {
        document.removeEventListener("pointermove", handleGlobalPointerMove);
        document.removeEventListener("touchmove", handleGlobalPointerMove);
        // Clean up RAF
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        pendingPositionRef.current = null;
      };
    }
  }, [isDragging, handleGlobalPointerMove]);

  // Handle drag start
  const handleDragStart = useCallback(
    (event) => {
      const { active } = event;
      const data = active.data.current;

      // Clear any previous moved exercise IDs
      setMovedExerciseIds(new Set());

      // Initialize pointer position from event if available (for immediate feedback)
      // The first move event will update it, but this provides instant initialization
      try {
        if (event?.activatorEvent) {
          const e = event.activatorEvent;
          const x = e.clientX ?? e.touches?.[0]?.clientX;
          const y = e.clientY ?? e.touches?.[0]?.clientY;
          if (x !== undefined && y !== undefined) {
            setPointerPosition({ x, y });
          }
        }
      } catch (e) {
        // Ignore if activatorEvent doesn't exist or isn't accessible
      }

      // Set dragging state to enable pointer tracking
      setIsDragging(true);

      // Set active drag item for context
      setActiveDragItem(data);

      // Set cursor to "move" during drag
      if (typeof document !== "undefined" && document.body) {
        document.body.style.cursor = "move";
        // Prevent text selection during drag
        document.body.style.userSelect = "none";
      }

      // Call kit handler
      kitDragStart(event);
    },
    [kitDragStart, setActiveDragItem],
  );

  // Handle drag move
  const handleDragMove = useCallback(
    (event) => {
      kitDragMove(event);
    },
    [kitDragMove],
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      // Stop dragging to disable pointer tracking
      setIsDragging(false);

      // Clear blocked toast
      clearBlockedMoveToast(hideToast);

      // Clear active drag item
      clearActiveDragItem();

      // Restore cursor and user selection
      if (typeof document !== "undefined" && document.body) {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }

      // Call kit handler
      kitDragEnd(event);

      // If no drop target, clear map and return
      if (!over) {
        dropPositionMap.clear();
        return;
      }

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!activeData || !overData) return;

      // Check if blocked
      if (isCrossWeekBlocked(activeData, overData.workoutWeek)) {
        return;
      }

      // For trailing drop zones, check canDrop logic (matching react-dnd)
      if (overData.type === "trailingDropZone") {
        // Check protocol slot restriction
        if (isCrossWeekBlocked(activeData, overData.workoutWeek)) {
          dropPositionMap.clear();
          return;
        }

        // If same workout, check if it's the last exercise (no-op)
        if (activeData.workoutId === overData.workoutId) {
          const endIndex = Math.max(0, overData.position - 1);
          if (activeData.index === endIndex) {
            // Don't allow dropping the last exercise at the end (no-op)
            dropPositionMap.clear();
            return;
          }
        }
      }

      const sourceWorkoutId = activeData.workoutId;
      const targetWorkoutId = overData.workoutId;
      const sourceIndex = activeData.index;

      // Get drop position based on drop zone type
      let dropPosition = null;
      if (overData.type === "exerciseTarget") {
        // Get drop position from map for exercise targets (above/below)
        dropPosition = dropPositionMap.get(
          `exercise-${overData.workoutId}-${overData.index}`,
        );
      } else if (
        overData.type === "trailingDropZone" ||
        overData.type === "emptyWorkoutDropZone"
      ) {
        // For trailing and empty zones, pass null as dropPosition (matching react-dnd)
        // The executeMove function will use the position from overData
        dropPosition = null;
      }

      // Execute move using shared utility
      const result = executeMove(
        activeData,
        overData,
        onMoveExercise,
        onMoveExerciseCrossWorkout,
        dropPosition,
      );

      // Set moved exercise IDs for animation
      if (result) {
        const movedIds =
          Array.isArray(result) && result.length > 0
            ? new Set(result)
            : new Set([activeData.exerciseId]);
        setMovedExerciseIds(movedIds);
        // Clear after animation completes
        setTimeout(() => setMovedExerciseIds(new Set()), 400);
      }

      // Clear drop position map after processing
      dropPositionMap.clear();
    },
    [
      kitDragEnd,
      clearActiveDragItem,
      hideToast,
      onMoveExercise,
      onMoveExerciseCrossWorkout,
    ],
  );

  // Handle drag cancel
  const handleDragCancel = useCallback(
    (event) => {
      // Stop dragging to disable pointer tracking
      setIsDragging(false);

      clearBlockedMoveToast(hideToast);
      clearActiveDragItem();
      // Clear drop position map
      dropPositionMap.clear();

      // Restore cursor and user selection
      if (typeof document !== "undefined" && document.body) {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }

      kitDragCancel();
    },
    [kitDragCancel, clearActiveDragItem, hideToast],
  );

  return (
    <MovedExerciseIdsContext.Provider value={movedExerciseIds}>
      <PointerPositionContext.Provider value={pointerPosition}>
        <DndContext
          sensors={sensors}
          collisionDetection={exerciseCollisionDetection}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {children}
        </DndContext>
      </PointerPositionContext.Provider>
    </MovedExerciseIdsContext.Provider>
  );
};

export default ExerciseDndProvider;
